import { useState, useEffect, useRef, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";

import {
  Box,
  SimpleGrid,
  Grid,
  VStack,
  HStack,
  Button,
  NativeSelect,
  Skeleton,
  Spinner,
  Text,
} from "@libs/design";
import {
  AlertTriangle,
  CheckCircle,
  XCircle,
  Calendar,
  Clock,
  Bell,
  FileText,
  RefreshCw,
  Activity,
  Zap,
  Target,
} from "@assets/icons";

import {
  KPICard,
  Header,
  Tabs,
  DQTrendChart,
  DomainBreakdownChart,
  MissingDataChart,
  ValidationSummary,
  AlertsTable,
  TicketsTable,
  Leaderboard,
  CountryGrid,
  FilterBar,
  CompanyTable,
} from "./components";
import {
  useGetKpi,
  useGetSummary,
  useGetTrends,
  useGetDomains,
  useGetMissingByCountry,
  useGetValidationSummary,
  useGetLeaderboard,
  useGetAlertsSummary,
  useGetAlerts,
  useGetCountries,
  useGetCompanies,
} from "./api";
import type {
  ApiResponse,
  KpiData,
  AlertsSummaryItem,
  AlertItem,
  CountryItem,
  CompanyItem,
} from "./api";
import {
  getTrend,
  mapAlertsResponse,
  mapCountriesResponse,
  mapCompaniesResponse,
  extractDataArray,
  getSummaryData,
  transformTrendsData,
  transformDomainsData,
  transformMissingByCountryData,
  transformValidationSummaryData,
  transformLeaderboardData,
} from "./utils";

const MONTH_SORT = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** Map UI severity filter to API param: all | critical | moderate */
function alertsSeverityToParam(value: string): string {
  if (value === "All Severities") return "all";
  return value.toLowerCase();
}

/** Build countries API params from dashboard filters */
function buildCountriesParams(filters: { region: string; status: string; domain: string }) {
  const params: Record<string, string> = {};
  if (filters.region && filters.region !== "All Regions") {
    params.region = filters.region;
  }
  if (filters.status && filters.status !== "All Status") {
    params.status = filters.status.toLowerCase();
  }
  if (filters.domain && filters.domain !== "All Domains") {
    params.domain = filters.domain.toLowerCase();
  }
  return params;
}

export default function DQDashboard() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("overview");
  const countriesSectionRef = useRef<HTMLDivElement>(null);

  const [filters, setFilters] = useState({
    region: "All Regions",
    status: "All Status",
    domain: "Purchase",
    month: "",
    year: "",
  });

  const [alertsSeverityFilter, setAlertsSeverityFilter] = useState<string>("All Severities");
  const countriesParams = useMemo(
    () => buildCountriesParams(filters),
    [filters.region, filters.status, filters.domain]
  );
  const alertsParams = useMemo(
    () => ({
      severity: alertsSeverityToParam(alertsSeverityFilter),
      status: "all" as const,
    }),
    [alertsSeverityFilter]
  );

  const { data: alertsSummaryRes } = useGetAlertsSummary();
  const alertsSummary: AlertsSummaryItem | undefined = (
    alertsSummaryRes as ApiResponse<AlertsSummaryItem> | undefined
  )?.data;

  const { data: alertsRes, isLoading: isAlertsLoading } = useGetAlerts({ params: alertsParams });
  const invalidateAlerts = () => {
    queryClient.invalidateQueries({ queryKey: ["dq-dashboard", "alerts"] });
  };

  const alertsList = useMemo(() => extractDataArray<AlertItem>(alertsRes), [alertsRes]);
  const alertsDataResolved = useMemo(
    () =>
      mapAlertsResponse(alertsList).sort((a, b) => {
        const aId = Number(a.id);
        const bId = Number(b.id);
        const safeA = Number.isNaN(aId) ? Number.MAX_SAFE_INTEGER : aId;
        const safeB = Number.isNaN(bId) ? Number.MAX_SAFE_INTEGER : bId;
        return safeA - safeB;
      }),
    [alertsList]
  );

  const { data: countriesRes, isLoading: isCountriesLoading } = useGetCountries({
    params: countriesParams,
  });
  const { data: countriesOptionsRes } = useGetCountries({ params: {} });
  const countriesList = useMemo(() => extractDataArray<CountryItem>(countriesRes), [countriesRes]);
  const countryGridDataResolved = useMemo(
    () => mapCountriesResponse(countriesList),
    [countriesList]
  );
  const countriesOptionsList = useMemo(
    () => extractDataArray<CountryItem>(countriesOptionsRes),
    [countriesOptionsRes]
  );

  const { data: companiesRes, isLoading: isCompaniesLoading } = useGetCompanies({
    params: { domain: "all" },
  });
  const companiesList = useMemo(() => extractDataArray<CompanyItem>(companiesRes), [companiesRes]);

  const companyDrilldownResolved = useMemo(
    () => mapCompaniesResponse(companiesList),
    [companiesList]
  );
  const handleFilterChange = (newFilters: {
    region: string;
    status: string;
    domain: string;
    month: string;
    year: string;
  }) => {
    setFilters(newFilters);
  };

  const countriesOptionsResolved = useMemo(
    () => mapCountriesResponse(countriesOptionsList),
    [countriesOptionsList]
  );
  const countriesFilterOptions = useMemo(() => {
    const data = countriesOptionsResolved;
    const isAll = (s: string | null | undefined) => s != null && String(s).toUpperCase() === "ALL";
    const allRegions = "All Regions";
    const regionSet = new Set(data.map((c) => c.subregion).filter(Boolean) as string[]);
    regionSet.delete(allRegions);
    const regions = [allRegions, ...regionSet].filter(
      (r): r is string => typeof r === "string" && !isAll(r)
    );
    const domainSet = new Set(data.map((c) => c.source).filter(Boolean) as string[]);
    const domains = [...domainSet].filter((d): d is string => typeof d === "string" && !isAll(d));
    const monthValues = [
      ...new Set(data.map((c) => c.month).filter((m): m is string => Boolean(m))),
    ];
    const months = monthValues.sort((a, b) => MONTH_SORT.indexOf(a) - MONTH_SORT.indexOf(b));
    const yearValues = [
      ...new Set(data.map((c) => c.year).filter((y) => y != null && !Number.isNaN(y))),
    ]
      .map(Number)
      .sort((a, b) => b - a);
    const years = yearValues;
    return { regions, domains, months, years };
  }, [countriesOptionsResolved]);

  // Example: Scroll to the countries section when it becomes active
  useEffect(() => {
    if (activeTab === "countries" && countriesSectionRef.current) {
      // Scroll the element into view smoothly
      countriesSectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [activeTab]);

  const scrollToCountriesSection = () => {
    if (countriesSectionRef.current) {
      countriesSectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const availableSeverities = ["Critical", "Moderate", "Healthy"];

  const alertCounts = useMemo(() => {
    if (alertsSummary) {
      return {
        total: alertsSummary.total ?? 0,
        critical: alertsSummary.critical ?? 0,
        pending: alertsSummary.pending ?? 0,
        resolvedToday: alertsSummary.resolvedToday ?? 0,
      };
    }
    return { total: 0, critical: 0, pending: 0, resolvedToday: 0 };
  }, [alertsSummary]);

  useEffect(() => {
    if (
      alertsSeverityFilter !== "All Severities" &&
      !availableSeverities.includes(alertsSeverityFilter)
    ) {
      setAlertsSeverityFilter("All Severities");
    }
  }, [alertsSeverityFilter, availableSeverities]);

  const getFilteredCountries = () => {
    let filtered = [...countryGridDataResolved];

    if (filters.status !== "All Status") {
      const statusLower = filters.status.toLowerCase();
      filtered = filtered.filter((c) => String(c.status).toLowerCase() === statusLower);
    }

    // Domain = source column. "All Domains" = show all rows; otherwise filter by source (case-insensitive)
    if (filters.domain && filters.domain !== "All Domains") {
      const domainLower = filters.domain.toLowerCase();
      filtered = filtered.filter((c) => String(c.source ?? "").toLowerCase() === domainLower);
    }

    // Region = subregion. "All Regions" = show all; otherwise filter (exclude only aggregate rows when "All Regions")
    if (filters.region === "All Regions") {
      filtered = filtered.filter((c) => String(c.subregion ?? "").toUpperCase() !== "ALL");
    } else if (filters.region) {
      filtered = filtered.filter(
        (c) => String(c.subregion ?? "").toLowerCase() === filters.region.toLowerCase()
      );
    }

    if (filters.month && filters.month !== "All Months") {
      filtered = filtered.filter((c) => c.month === filters.month);
    }

    if (filters.year && filters.year !== "All Years") {
      const yearNum = Number(filters.year);
      if (!Number.isNaN(yearNum)) {
        filtered = filtered.filter((c) => c.year === yearNum);
      }
    }

    return filtered;
  };

  const { data: kpiResponse, isLoading: isKpiLoading, isError: isKpiError } = useGetKpi();
  const { data: summaryRes, isLoading: isSummaryLoading } = useGetSummary();
  const { data: trendsRes, isLoading: isTrendsLoading } = useGetTrends();
  const { data: domainsRes, isLoading: isDomainsLoading } = useGetDomains();
  const { data: missingRes, isLoading: isMissingLoading } = useGetMissingByCountry();
  const { data: validationRes, isLoading: isValidationLoading } = useGetValidationSummary();
  const { data: leaderboardRes, isLoading: isLeaderboardLoading } = useGetLeaderboard();
  const kpiData: KpiData | undefined = (kpiResponse as ApiResponse<KpiData> | undefined)?.data;
  const summaryApi = summaryRes?.data?.[0];
  const trendsApi = trendsRes?.data ?? [];
  const domainsApi = domainsRes?.data ?? [];
  const missingByCountryApi = missingRes?.data ?? [];
  const validationSummaryApi = validationRes?.data ?? [];
  const leaderboardApi = leaderboardRes?.data ?? [];
  const dqTrendDataResolved = useMemo(() => transformTrendsData(trendsApi, []), [trendsApi]);
  const domainBreakdownResolved = useMemo(() => transformDomainsData(domainsApi, []), [domainsApi]);
  const missingByCountryResolved = useMemo(
    () => transformMissingByCountryData(missingByCountryApi, []),
    [missingByCountryApi]
  );
  const validationSummaryResolved = useMemo(
    () => transformValidationSummaryData(validationSummaryApi, []),
    [validationSummaryApi]
  );
  const leaderboardDataResolved = useMemo(
    () => transformLeaderboardData(leaderboardApi, []),
    [leaderboardApi]
  );
  const summaryData = useMemo(() => getSummaryData(summaryApi), [summaryApi]);
  const overviewOpenTickets = useMemo(() => {
    const fromKpi = Number(kpiData?.openTickets);
    if (kpiData?.openTickets != null && !Number.isNaN(fromKpi)) return fromKpi;

    const fromSummaryCamel = Number(summaryData?.openTickets);
    if (!Number.isNaN(fromSummaryCamel)) return fromSummaryCamel;

    const fromSummarySnake = Number(summaryData?.open_tickets);
    if (!Number.isNaN(fromSummarySnake)) return fromSummarySnake;

    return null;
  }, [summaryData, kpiData?.openTickets]);
  const openTicketsOverviewTrend = useMemo(() => {
    if (isKpiLoading || kpiData == null) return null;
    return getTrend(kpiData.pctChange, "%");
  }, [isKpiLoading, kpiData]);
  const ticketPctChangeLabel = useMemo(() => {
    const raw = kpiData?.pctChange;
    if (raw == null) return "—";
    const parsed = Number(raw);
    if (Number.isNaN(parsed)) return "—";
    return `${parsed.toFixed(2)}%`;
  }, [kpiData?.pctChange]);
  const overallTrend = getTrend(summaryData?.overallDqScoreChange as number | undefined, "%");
  const freshnessTrend = getTrend(summaryData?.freshnessScoreChange as number | undefined, "%");
  const countriesTrend = getTrend(summaryData?.countriesWithIssuesChange as number | undefined);
  const missingTrend = getTrend(summaryData?.missingDaysChange as number | undefined);
  return (
    <Box minH="100vh" style={{ backgroundColor: "#F8F9FA" }}>
      <Header />
      <Box px={6} py={4}>
        <Tabs activeTab={activeTab} onTabChange={setActiveTab} />
      </Box>

      <Box p={6}>
        {activeTab === "overview" && (
          <VStack gap={6} align="stretch">
            {/* KPI Row */}
            <SimpleGrid columns={{ base: 1, md: 2, lg: 5 }} gap={4} alignItems="stretch">
              {isSummaryLoading ? (
                <>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Box
                      key={i}
                      bg="white"
                      borderRadius="xl"
                      borderWidth="1px"
                      borderColor="gray.200"
                      p={4}
                      boxShadow="sm"
                      minH={32}
                      h="100%"
                    >
                      <HStack justify="space-between" align="flex-start">
                        <Box flex={1}>
                          <Skeleton h={3} w="60%" mb={3} />
                          <Skeleton h={6} w="40%" mb={2} />
                          <Skeleton h={3} w="70%" />
                        </Box>
                        <Skeleton borderRadius="full" boxSize={10} flexShrink={0} />
                      </HStack>
                    </Box>
                  ))}
                </>
              ) : (
                <>
                  <Box minH={32} h="100%">
                    <KPICard
                      title="Overall DQ Score"
                      value={
                        summaryData?.overallDqScore != null
                          ? `${Number(summaryData.overallDqScore).toFixed(1)}%`
                          : "—"
                      }
                      subtitle="Across all countries"
                      icon={Target}
                      trend={overallTrend.trend}
                      trendValue={overallTrend.value}
                      color="blue"
                    />
                  </Box>
                  <Box minH={32} h="100%">
                    <KPICard
                      title="Data Freshness"
                      value={
                        summaryData?.avgFreshnessScore != null
                          ? `${Number(summaryData.avgFreshnessScore).toFixed(1)}%`
                          : "—"
                      }
                      subtitle="Within SLA"
                      icon={Clock}
                      trend={freshnessTrend.trend}
                      trendValue={freshnessTrend.value}
                      color="green"
                    />
                  </Box>
                  <Box minH={32} h="100%">
                    <KPICard
                      title="Countries with Issues"
                      value={String(summaryData?.countriesWithIssues ?? "—")}
                      subtitle="RO, RS, BA"
                      icon={AlertTriangle}
                      trend={countriesTrend.trend}
                      trendValue={countriesTrend.value}
                      color="red"
                    />
                  </Box>
                  <Box minH={32} h="100%">
                    <KPICard
                      title="Total Missing Days"
                      value={String(summaryData?.totalMissingDays ?? "—")}
                      subtitle="Across all regions"
                      icon={Calendar}
                      trend={missingTrend.trend}
                      trendValue={missingTrend.value}
                      color="yellow"
                    />
                  </Box>
                  <Box minH={32} h="100%">
                    <KPICard
                      title="Open Tickets"
                      value={String(overviewOpenTickets ?? "—")}
                      subtitle={isKpiLoading ? undefined : (kpiData?.severitySummary ?? "—")}
                      icon={FileText}
                      trend={openTicketsOverviewTrend?.trend}
                      trendValue={openTicketsOverviewTrend?.value}
                      color="purple"
                    />
                  </Box>
                </>
              )}
            </SimpleGrid>

            {/* Charts Row 1 */}
            <Grid templateColumns={{ base: "1fr", lg: "2fr 1fr" }} gap={6} alignItems="stretch">
              <Box
                position="relative"
                minH={380}
                bg="white"
                borderRadius="xl"
                borderWidth="1px"
                borderColor="gray.200"
                boxShadow="sm"
                overflow="hidden"
                userSelect="none"
                css={{ "& *:focus": { outline: "none" } }}
              >
                <DQTrendChart data={dqTrendDataResolved} />
                {isTrendsLoading && (
                  <Box
                    position="absolute"
                    inset={0}
                    bg="whiteAlpha.800"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    zIndex={1}
                  >
                    <Spinner size="xl" color="green.500" />
                  </Box>
                )}
                {!isTrendsLoading && dqTrendDataResolved.length === 0 && (
                  <Box
                    position="absolute"
                    inset={0}
                    bg="white"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    zIndex={1}
                  >
                    <Text fontSize="sm" color="gray.500">
                      No data available
                    </Text>
                  </Box>
                )}
              </Box>
              <Box
                position="relative"
                minH={380}
                bg="white"
                borderRadius="xl"
                borderWidth="1px"
                borderColor="gray.200"
                boxShadow="sm"
                overflow="hidden"
                userSelect="none"
                css={{ "& *:focus": { outline: "none" } }}
              >
                <DomainBreakdownChart data={domainBreakdownResolved} />
                {isDomainsLoading && (
                  <Box
                    position="absolute"
                    inset={0}
                    bg="whiteAlpha.800"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    zIndex={1}
                  >
                    <Spinner size="xl" color="green.500" />
                  </Box>
                )}
                {!isDomainsLoading && domainBreakdownResolved.length === 0 && (
                  <Box
                    position="absolute"
                    inset={0}
                    bg="white"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    zIndex={1}
                  >
                    <Text fontSize="sm" color="gray.500">
                      No data available
                    </Text>
                  </Box>
                )}
              </Box>
            </Grid>

            {/* Charts Row 2 */}
            <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={6} alignItems="stretch">
              <Box
                position="relative"
                minH={380}
                bg="white"
                borderRadius="xl"
                borderWidth="1px"
                borderColor="gray.200"
                boxShadow="sm"
                overflow="hidden"
                userSelect="none"
                css={{ "& *:focus": { outline: "none" } }}
              >
                <MissingDataChart data={missingByCountryResolved} />
                {isMissingLoading && (
                  <Box
                    position="absolute"
                    inset={0}
                    bg="whiteAlpha.800"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    zIndex={1}
                  >
                    <Spinner size="xl" color="green.500" />
                  </Box>
                )}
                {!isMissingLoading && missingByCountryResolved.length === 0 && (
                  <Box
                    position="absolute"
                    inset={0}
                    bg="white"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    zIndex={1}
                  >
                    <Text fontSize="sm" color="gray.500">
                      No data available
                    </Text>
                  </Box>
                )}
              </Box>
              <Box
                position="relative"
                minH={380}
                bg="white"
                borderRadius="xl"
                borderWidth="1px"
                borderColor="gray.200"
                boxShadow="sm"
                overflow="hidden"
                userSelect="none"
                css={{ "& *:focus": { outline: "none" } }}
              >
                <ValidationSummary data={validationSummaryResolved} />
                {isValidationLoading && (
                  <Box
                    position="absolute"
                    inset={0}
                    bg="whiteAlpha.800"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    zIndex={1}
                  >
                    <Spinner size="xl" color="green.500" />
                  </Box>
                )}
                {!isValidationLoading && validationSummaryResolved.length === 0 && (
                  <Box
                    position="absolute"
                    inset={0}
                    bg="white"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    zIndex={1}
                  >
                    <Text fontSize="sm" color="gray.500">
                      No data available
                    </Text>
                  </Box>
                )}
              </Box>
            </Grid>

            {/* Bottom Section */}
            <Grid templateColumns={{ base: "1fr", lg: "2fr 1fr" }} gap={6} alignItems="stretch">
              <Box
                position="relative"
                minH={340}
                bg="white"
                borderRadius="xl"
                borderWidth="1px"
                borderColor="gray.200"
                boxShadow="sm"
                overflow="hidden"
                userSelect="none"
                css={{ "& *:focus": { outline: "none" } }}
              >
                {isAlertsLoading ? (
                  <Box p={6}>
                    <Skeleton h={5} w="30%" mb={2} />
                    <Skeleton h={3} w="50%" mb={6} />
                    <VStack gap={2} align="stretch">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <Skeleton key={i} h={12} w="100%" borderRadius="md" />
                      ))}
                    </VStack>
                  </Box>
                ) : alertsDataResolved.length === 0 ? (
                  <Box p={6} display="flex" alignItems="center" justifyContent="center" minH={280}>
                    <Text fontSize="sm" color="gray.500">
                      No data available
                    </Text>
                  </Box>
                ) : (
                  <AlertsTable
                    data={alertsDataResolved}
                    embedded
                    onTicketCreated={invalidateAlerts}
                  />
                )}
              </Box>
              <Box
                position="relative"
                minH={340}
                bg="white"
                borderRadius="xl"
                borderWidth="1px"
                borderColor="gray.200"
                boxShadow="sm"
                overflow="hidden"
                userSelect="none"
                css={{ "& *:focus": { outline: "none" } }}
              >
                {isLeaderboardLoading ? (
                  <Box p={6}>
                    <Skeleton h={5} w="40%" mb={2} />
                    <Skeleton h={3} w="55%" mb={6} />
                    <VStack gap={2} align="stretch">
                      {[1, 2, 3, 4].map((i) => (
                        <Skeleton key={i} h={14} w="100%" borderRadius="md" />
                      ))}
                    </VStack>
                  </Box>
                ) : leaderboardDataResolved.length === 0 ? (
                  <Box p={6} display="flex" alignItems="center" justifyContent="center" minH={280}>
                    <Text fontSize="sm" color="gray.500">
                      No data available
                    </Text>
                  </Box>
                ) : (
                  <Leaderboard data={leaderboardDataResolved} embedded />
                )}
              </Box>
            </Grid>
          </VStack>
        )}

        {activeTab === "countries" && (
          <VStack ref={countriesSectionRef} gap={6} align="stretch" id="countries-section">
            <FilterBar
              onFilterChange={handleFilterChange}
              availableRegions={countriesFilterOptions.regions}
              availableDomains={countriesFilterOptions.domains}
              availableMonths={countriesFilterOptions.months}
              availableYears={countriesFilterOptions.years}
            />
            <Box position="relative" minH={480} overflow="hidden">
              {isCountriesLoading ? (
                <Box p={6}>
                  <Skeleton h={5} w="35%" mb={2} />
                  <Skeleton h={3} w="55%" mb={6} />
                  <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={4}>
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <Skeleton key={i} h={32} w="100%" borderRadius="xl" />
                    ))}
                  </SimpleGrid>
                </Box>
              ) : countryGridDataResolved.length === 0 ? (
                <Box p={6} display="flex" alignItems="center" justifyContent="center" minH={400}>
                  <Text fontSize="sm" color="gray.500">
                    No data available
                  </Text>
                </Box>
              ) : (
                <CountryGrid data={getFilteredCountries()} />
              )}
            </Box>
            {/* Example: Button to demonstrate ref functionality */}
            <Button onClick={() => scrollToCountriesSection()} size="sm" variant="outline">
              Scroll to Top
            </Button>
          </VStack>
        )}
        {activeTab === "company" && (
          <VStack gap={6} align="stretch">
            <Box
              position="relative"
              minH={400}
              bg="white"
              borderRadius="xl"
              borderWidth="1px"
              borderColor="gray.200"
              boxShadow="sm"
              overflow="hidden"
            >
              {isCompaniesLoading ? (
                <Box p={6}>
                  <Skeleton h={5} w="40%" mb={2} />
                  <Skeleton h={3} w="55%" mb={6} />
                  <VStack gap={2} align="stretch">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Skeleton key={i} h={12} borderRadius="md" />
                    ))}
                  </VStack>
                </Box>
              ) : companyDrilldownResolved.length === 0 ? (
                <Box p={6} display="flex" alignItems="center" justifyContent="center" minH={300}>
                  <Text fontSize="sm" color="gray.500">
                    No data available
                  </Text>
                </Box>
              ) : (
                <CompanyTable data={companyDrilldownResolved} embedded />
              )}
            </Box>
          </VStack>
        )}

        {activeTab === "alerts" && (
          <VStack gap={6} align="stretch">
            {/* Alert Stats */}
            <SimpleGrid columns={{ base: 1, md: 4 }} gap={4}>
              <KPICard
                title="Total Alerts"
                value={String(alertCounts.total)}
                subtitle="Today"
                icon={Bell}
                color="blue"
              />
              <KPICard
                title="Critical"
                value={String(alertCounts.critical)}
                subtitle="Requires immediate action"
                icon={XCircle}
                color="red"
              />
              <KPICard
                title="Pending"
                value={String(alertCounts.pending)}
                subtitle="Awaiting ticket creation"
                icon={Clock}
                color="yellow"
              />
              <KPICard
                title="Resolved Today"
                value={String(alertCounts.resolvedToday)}
                subtitle="Successfully closed"
                icon={CheckCircle}
                color="green"
              />
            </SimpleGrid>

            {/* Alerts Table */}
            <Box bg="white" borderRadius="xl" borderWidth="1px" boxShadow="sm" overflow="hidden">
              <Box p={6} borderBottomWidth="1px" borderColor="gray.100">
                <HStack justify="space-between" align="center" wrap="wrap" gap={4}>
                  <Box>
                    <Box fontSize="lg" fontWeight="semibold" color="gray.900" as="h3">
                      All Alerts
                    </Box>
                    <Box fontSize="sm" color="gray.500" as="p">
                      Complete alert log with status tracking
                    </Box>
                  </Box>
                  <HStack gap={3} wrap="wrap" align="center">
                    <NativeSelect.Root
                      fontSize="sm"
                      borderWidth="1px"
                      borderColor="gray.200"
                      borderRadius="lg"
                      bg="white"
                      w={40}
                      css={{
                        "&:focus-within": {
                          boxShadow: "none",
                          outline: "none",
                        },
                      }}
                    >
                      <NativeSelect.Field
                        value={alertsSeverityFilter}
                        onChange={(e) => setAlertsSeverityFilter(e.target.value)}
                      >
                        <option value="All Severities">All Severities</option>
                        {availableSeverities.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </NativeSelect.Field>
                    </NativeSelect.Root>
                    <HStack>
                      <Box as="span" color="gray.600" display="inline-flex" alignItems="center">
                        <RefreshCw />
                      </Box>
                      <Button
                        fontSize="sm"
                        onClick={() => setAlertsSeverityFilter("All Severities")}
                      >
                        Refresh
                      </Button>
                    </HStack>
                  </HStack>
                </HStack>
              </Box>
            </Box>
            <AlertsTable
              data={alertsDataResolved}
              showFullTable
              onTicketCreated={invalidateAlerts}
            />
          </VStack>
        )}
        {activeTab === "tickets" && (
          <VStack gap={6} align="stretch">
            {/* Ticket Stats */}
            <SimpleGrid columns={{ base: 1, md: 4 }} gap={4}>
              {isKpiLoading ? (
                [1, 2, 3, 4].map((i) => (
                  <Box
                    key={i}
                    bg="white"
                    borderRadius="xl"
                    borderWidth="1px"
                    borderColor="gray.200"
                    p={4}
                    boxShadow="sm"
                    minH={24}
                  >
                    <Skeleton h={4} w="50%" mb={3} />
                    <Skeleton h={6} w="30%" />
                  </Box>
                ))
              ) : isKpiError ? (
                <Box gridColumn="span 4">
                  <Text color="red.500">Failed to load KPI</Text>
                </Box>
              ) : (
                <>
                  <KPICard
                    title="Open Tickets"
                    value={String(kpiData?.openTickets ?? "—")}
                    subtitle="Across all countries"
                    icon={FileText}
                    color="blue"
                  />

                  <KPICard
                    title="Severity Summary"
                    value={kpiData?.severitySummary ?? "—"}
                    subtitle="Current open tickets"
                    icon={Activity}
                    color="yellow"
                  />

                  <KPICard
                    title="Percent Change"
                    value={ticketPctChangeLabel}
                    subtitle="Compared to previous month"
                    icon={CheckCircle}
                    color="green"
                  />

                  <KPICard
                    title="Previous Month Tickets"
                    value={String(kpiData?.prevMonthTickets ?? "—")}
                    subtitle="Total tickets last month"
                    icon={Zap}
                    color="purple"
                  />
                </>
              )}
            </SimpleGrid>

            {/* Tickets Table */}
            <TicketsTable />
          </VStack>
        )}
      </Box>
    </Box>
  );
}
